import AdminLayout from "@/components/AdminLayout";
import { PageHeader } from "@/components/ui-system";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { ShoppingBag, KeyRound, RefreshCw, Webhook, Settings, AlertTriangle, MapPinned, Code2, Link2 } from "lucide-react";

const Section = ({ id, icon: Icon, title, children }) => (
  <Card className="border-0 shadow-sm scroll-mt-24" id={id}>
    <CardContent className="pt-6">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-slate-800 mb-3">
        {Icon && <Icon className="w-5 h-5 text-orange-600" />} {title}
      </h2>
      <div className="space-y-3 text-sm text-slate-600 leading-relaxed">{children}</div>
    </CardContent>
  </Card>
);
const Code = ({ children }) => (
  <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 text-xs overflow-x-auto font-mono whitespace-pre">{children}</pre>
);
const Mono = ({ children }) => <code className="font-mono text-[13px] bg-slate-100 text-slate-800 rounded px-1 py-0.5">{children}</code>;
const TH = ({ children }) => <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600 border-b">{children}</th>;
const TD = ({ children, mono }) => <td className={`px-3 py-2 align-top border-b border-slate-100 ${mono ? "font-mono text-[13px]" : ""}`}>{children}</td>;

const TOC = [
  ["overview", "Overview"], ["connect", "How a seller connects"], ["architecture", "Architecture"],
  ["sync", "Sync mechanism"], ["contract", "Magento API contract"], ["mapping", "Order field mapping"],
  ["config", "Configuration (env)"], ["endpoints", "EdgeLMS API endpoints"], ["limits", "Limits & behaviour"],
  ["troubleshooting", "Troubleshooting"], ["roadmap", "Roadmap"], ["related", "Related guides"],
];

const MagentoDevDocs = ({ user, onLogout }) => (
  <AdminLayout user={user} onLogout={onLogout}>
    <div className="space-y-6 w-full max-w-4xl">
      <PageHeader
        title="Magento Integration — Developer Guide"
        subtitle="How the B2C Sales-Channel Magento 2 connector works, end to end"
        badge={<Badge variant="outline" className="border-orange-200 text-orange-700">B2C · Channels</Badge>}
      />

      <Card className="border-0 shadow-sm">
        <CardContent className="pt-6">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">On this page</p>
          <div className="flex flex-wrap gap-2">
            {TOC.map(([id, label]) => (
              <a key={id} href={`#${id}`} className="text-xs rounded-full bg-slate-100 hover:bg-orange-50 hover:text-orange-700 px-2.5 py-1 text-slate-600 transition-colors">{label}</a>
            ))}
          </div>
        </CardContent>
      </Card>

      <Section id="overview" icon={ShoppingBag} title="1. Overview">
        <p>A seller connects their <strong>Magento 2</strong> store and its orders <strong>auto-sync into B2C</strong> as <Mono>NEW</Mono> (unshipped) orders, shipped through the normal B2C flow. <strong>One-way for v1</strong> (Magento → EdgeLMS). Shares the same channel foundation as Shopify / WooCommerce.</p>
        <p className="rounded-lg bg-orange-50 border border-orange-200 p-3 text-orange-800"><strong>Poll-only:</strong> Magento Open Source has <strong>no native order webhooks</strong>, so this channel syncs on the poll cron (~every 15 min) — there is no real-time push.</p>
      </Section>

      <Section id="connect" icon={KeyRound} title="2. How a seller connects (Integration access token)">
        <p>We use a Magento <strong>Integration access token</strong>, sent as a Bearer header. Steps shown to the seller on the connect screen:</p>
        <ol className="list-decimal list-inside space-y-1.5">
          <li>Magento admin → <strong>System → Extensions → Integrations → Add New Integration</strong>.</li>
          <li>Name it "EdgeLMS". Under <strong>API</strong>, grant access to <strong>Sales (Orders)</strong> and <strong>Catalog</strong> (read).</li>
          <li><strong>Save</strong>, then <strong>Activate</strong> — Magento shows the tokens; copy the <strong>Access Token</strong>.</li>
          <li>Ensure the store is on <strong>HTTPS</strong> and the REST API is reachable at <Mono>/rest/V1</Mono>.</li>
          <li>EdgeLMS → <strong>Sales Channels → Connect Magento</strong> → paste store URL (<Mono>https://your-store.com</Mono>) + the Access Token, pick a default pickup location + default package, Connect.</li>
        </ol>
        <p className="text-slate-500">The token is <strong>encrypted at rest</strong> and never shown again.</p>
      </Section>

      <Section id="architecture" icon={Code2} title="3. Architecture">
        <ul className="list-disc list-inside space-y-1.5">
          <li><strong>Collection</strong> <Mono>b2c_channels</Mono> (<Mono>EDGCH-</Mono> id, <Mono>type: "magento"</Mono>) — store_url, <strong>encrypted credentials</strong> (access token), default pickup + package, <Mono>sku_weights</Mono>, <Mono>sync_cursor</Mono>, stats. <Mono>webhook_id</Mono> is always null (no webhooks).</li>
          <li><strong>Encryption</strong> — <Mono>utils/secret_box.py</Mono> (Fernet).</li>
          <li><strong>Adapter</strong> — <Mono>services/b2c/channels/magento_adapter.py</Mono>.</li>
          <li><strong>Service</strong> — <Mono>services/b2c/channel_service.py</Mono> (shared; dispatches by type).</li>
          <li><strong>Ingestion</strong> → a <Mono>b2c_orders</Mono> doc at <Mono>NEW</Mono>, <strong>deduped</strong> on <Mono>(seller_id, channel_id, external_order_id)</Mono>.</li>
        </ul>
      </Section>

      <Section id="sync" icon={RefreshCw} title="4. Sync mechanism (poll only)">
        <ul className="list-disc list-inside space-y-1.5">
          <li><strong>Poll cron</strong> — <Mono>b2c_channel_sync_job</Mono> every <Mono>B2C_CHANNEL_SYNC_MINUTES</Mono> (default 15). Fetches orders with <Mono>created_at &gt; cursor</Mono>, sorted ascending, paginating by <Mono>searchCriteria[currentPage]</Mono> + <Mono>total_count</Mono>, ingests, advances the cursor.</li>
          <li><strong>No webhook</strong> — Magento has no built-in order webhooks; the poll is the only path. Use <strong>Sync now</strong> to pull immediately.</li>
        </ul>
        <p>Dedupe on the Magento order id makes re-polling safe — an order is created exactly once.</p>
      </Section>

      <Section id="contract" icon={Webhook} title="5. Magento API contract used">
        <ul className="list-disc list-inside space-y-1.5">
          <li><strong>REST API</strong> · base <Mono>https://store/rest/V1</Mono> · <strong>Auth</strong> <Mono>Authorization: Bearer &lt;access_token&gt;</Mono>.</li>
          <li><Mono>GET /orders?searchCriteria[pageSize]=1</Mono> — connection check.</li>
          <li><Mono>GET /orders</Mono> with <Mono>searchCriteria</Mono>: a <Mono>created_at gt</Mono> filter, <Mono>sortOrders[created_at]=ASC</Mono>, <Mono>pageSize=100</Mono>, <Mono>currentPage=N</Mono>. Response <Mono>&#123; items, total_count &#125;</Mono>.</li>
        </ul>
        <Code>{`GET /rest/V1/orders
  ?searchCriteria[filterGroups][0][filters][0][field]=created_at
  &searchCriteria[filterGroups][0][filters][0][value]=2026-06-17 11:00:00
  &searchCriteria[filterGroups][0][filters][0][conditionType]=gt
  &searchCriteria[sortOrders][0][field]=created_at
  &searchCriteria[sortOrders][0][direction]=ASC
  &searchCriteria[pageSize]=100&searchCriteria[currentPage]=1`}</Code>
      </Section>

      <Section id="mapping" icon={ShoppingBag} title="6. Order field mapping (Magento → B2C)">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-slate-100 rounded">
            <thead className="bg-slate-50"><tr><TH>Magento field</TH><TH>B2C field</TH><TH>Notes</TH></tr></thead>
            <tbody>
              <tr><TD mono>entity_id</TD><TD mono>external_order_id</TD><TD>Dedupe key</TD></tr>
              <tr><TD mono>increment_id</TD><TD mono>external_order_number</TD><TD>e.g. "000000777"</TD></tr>
              <tr><TD mono>shipping_assignments / billing_address</TD><TD mono>buyer</TD><TD>shipping used, falling back to billing; street[] joined; region→state, postcode→pincode</TD></tr>
              <tr><TD mono>items[]</TD><TD mono>products[]</TD><TD>name, sku, price→unit_price, qty_ordered→qty; child rows (parent_item_id) skipped to avoid double-count</TD></tr>
              <tr><TD mono>payment.method</TD><TD mono>payment_mode</TD><TD>COD if "cashondelivery" / "cod"; else prepaid</TD></tr>
              <tr><TD mono>grand_total</TD><TD mono>cod_amount</TD><TD>Buyer's full due, for COD only</TD></tr>
              <tr><TD>Σ items</TD><TD mono>order_value</TD><TD>Goods value (matches the manual-order path)</TD></tr>
              <tr><TD>SKU catalog / default</TD><TD mono>package</TD><TD>Per-SKU weight when every SKU is in the catalog, else the channel default package</TD></tr>
            </tbody>
          </table>
        </div>
      </Section>

      <Section id="config" icon={Settings} title="7. Configuration (environment)">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-slate-100 rounded">
            <thead className="bg-slate-50"><tr><TH>Env var</TH><TH>Purpose</TH><TH>Default</TH></tr></thead>
            <tbody>
              <tr><TD mono>B2C_CHANNEL_SECRET_KEY</TD><TD>Key for encrypting channel credentials.</TD><TD mono>JWT_SECRET_KEY</TD></tr>
              <tr><TD mono>B2C_CHANNEL_SYNC_MINUTES</TD><TD>Poll interval for the channel-sync cron (≤0 disables). Magento relies entirely on this.</TD><TD mono>15</TD></tr>
            </tbody>
          </table>
        </div>
        <p className="text-slate-500"><Mono>PUBLIC_API_BASE_URL</Mono> is not needed for Magento (no webhooks).</p>
      </Section>

      <Section id="endpoints" icon={Code2} title="8. EdgeLMS API endpoints">
        <Code>{`GET    /api/b2c/seller/channels                      list connected channels
POST   /api/b2c/seller/channels/magento              connect a Magento store
GET    /api/b2c/seller/channels/{id}                 channel detail
PUT    /api/b2c/seller/channels/{id}                 update (auto_sync, pickup, package, sku_weights)
DELETE /api/b2c/seller/channels/{id}                 disconnect
POST   /api/b2c/seller/channels/{id}/sync            manual sync now
POST   /api/b2c/seller/channels/{id}/verify          re-verify the connection`}</Code>
        <p>No webhook endpoint for Magento. Seller endpoints are gated by <Mono>b2c.channels:view</Mono> / <Mono>b2c.channels:manage</Mono> and scoped to the seller.</p>
      </Section>

      <Section id="limits" icon={AlertTriangle} title="9. Limits & behaviour">
        <ul className="list-disc list-inside space-y-1.5">
          <li><strong>Poll-only (~15 min latency)</strong> — no real-time order push (Magento has no webhooks).</li>
          <li><strong>HTTPS required</strong> — token auth needs HTTPS.</li>
          <li><strong>One-way (v1)</strong> — no fulfillment/AWB write-back to Magento yet.</li>
          <li><strong>India-focused</strong> — buyer phone reduced to its last 10 digits, pincodes treated as Indian; an international order imports as <Mono>NEW</Mono> with <Mono>zone = null</Mono>.</li>
          <li><MapPinned className="inline w-4 h-4 text-slate-400 mr-1" /><strong>Unserviceable / invalid pincode</strong> — imports as NEW but <strong>ship is blocked with a clear 422</strong>; the seller fixes the address, then ships.</li>
          <li><strong>Cursor timezone</strong> — the <Mono>created_at</Mono> cursor uses Magento's own datetime format; if the store clock differs materially from UTC, the first poll window may shift slightly (dedupe + repeated polls self-correct).</li>
        </ul>
      </Section>

      <Section id="troubleshooting" icon={AlertTriangle} title="10. Troubleshooting">
        <ul className="list-disc list-inside space-y-1.5">
          <li><strong>"Magento rejected the access token"</strong> — the integration isn't activated, the token is wrong, or it lacks Sales/Orders read access. Re-activate the integration and reconnect.</li>
          <li><strong>"REST API not found"</strong> — wrong store URL (use the base URL) or the REST API is blocked (web-server rules / security module).</li>
          <li><strong>Orders not appearing</strong> — confirm the channel is <Mono>Connected</Mono> and <Mono>Auto-sync</Mono> is on; use <strong>Sync now</strong> (poll runs every 15 min).</li>
          <li><strong>Wrong order totals on configurables</strong> — child line items are skipped (only parent lines count toward <Mono>order_value</Mono>); the COD amount uses Magento's <Mono>grand_total</Mono>.</li>
          <li><strong>Can't ship a synced order</strong> — a 422 about pincode/weight means the buyer address or package needs fixing on that order first.</li>
          <li><strong>404 on a channel call</strong> — the channel id is wrong or the channel was disconnected; refresh the Sales Channels list.</li>
          <li><strong>Other Magento 4xx/5xx</strong> — any non-2xx from <Mono>/rest/V1</Mono> surfaces as the channel's <Mono>last_error</Mono>; check the Magento server logs and the integration's granted resources, then <strong>Sync now</strong> / re-verify.</li>
        </ul>
      </Section>

      <Section id="roadmap" icon={RefreshCw} title="11. Roadmap">
        <p>Next: <strong>Custom-API</strong> channel (push orders via an EdgeLMS key) and <strong>fulfillment write-back</strong> to the stores (mark complete + push the AWB — Air Waybill / tracking number). A push-based Magento integration (extension/event) could add real-time sync later.</p>
      </Section>

      <Section id="related" icon={Link2} title="12. Related guides">
        <p>Sibling channel integration guides (same connector foundation):</p>
        <ul className="list-disc list-inside space-y-1.5">
          <li><Link className="text-orange-700 underline" to="/admin/dev-docs/shopify">Shopify Integration</Link></li>
          <li><Link className="text-orange-700 underline" to="/admin/dev-docs/woocommerce">WooCommerce Integration</Link></li>
          <li><Link className="text-orange-700 underline" to="/admin/dev-docs/custom-api">Custom / API Integration</Link></li>
        </ul>
        <p>The seller-facing 5-step <strong>connect guide</strong> (shown on <Mono>Sales Channels → Connect</Mono>) is defined in <Mono>models/b2c_channel.py → CHANNEL_CATALOG</Mono>. Keep its steps in sync with §2 above.</p>
      </Section>
    </div>
  </AdminLayout>
);

export default MagentoDevDocs;
